/**
 * Рендерит лидерборд топ-30 игроков в PNG 1080x1080 и инициирует скачивание.
 * Чистый клиент, без сетевых запросов — данные приходят аргументом.
 */

const W = 1080
const H = 1080

const COLORS = {
  bgInner: '#1a1340',
  bgOuter: '#08081a',
  text: '#e8eafc',
  accent: '#00ffd0',
  gold: '#ffd84a',
  silver: '#d8d8e0',
  bronze: '#ff9b5a',
  divider: 'rgba(255,255,255,0.05)',
  muted: 'rgba(232,234,252,0.7)',
}

const CLUSTER_LABEL = {
  music: 'МУЗЫКА',
  movies: 'КИНО',
  other: 'ДРУГОЕ',
}

const LAYOUT = {
  paddingX: 56,
  headerY: 60,
  titleY: 140,
  gridTop: 240,
  gridBottom: 1020,
  columnGap: 40,
  footerY: 1040,
}
LAYOUT.rowHeight = (LAYOUT.gridBottom - LAYOUT.gridTop) / 15

const FONTS = {
  brand: 'italic 900 28px "Inter", system-ui, sans-serif',
  title: 'italic 900 64px "Inter", system-ui, sans-serif',
  place: '800 22px "Inter", system-ui, sans-serif',
  name: '700 22px "Inter", system-ui, sans-serif',
  rating: '800 24px "Inter", system-ui, sans-serif',
  gw: '600 14px "Inter", system-ui, sans-serif',
  footer: '600 18px "Inter", system-ui, sans-serif',
}

const SKEW_X = -0.07

export async function renderRatingBlob({ players, cluster }) {
  if (!Array.isArray(players) || players.length === 0) {
    throw new Error('No players to export')
  }

  await ensureFontsReady()

  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  ctx.imageSmoothingQuality = 'high'

  drawBackground(ctx)
  drawHeader(ctx, cluster)
  drawTitle(ctx)
  drawRows(ctx, players)
  drawFooter(ctx)

  return canvasToBlob(canvas)
}

export default async function exportRatingImage({ players, cluster }) {
  const blob = await renderRatingBlob({ players, cluster })
  triggerDownload(blob, fileName(cluster))
}

async function ensureFontsReady() {
  if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready
    } catch (_) {
      // если по какой-то причине шрифты не готовы — продолжаем с системным фоллбеком
    }
  }
}

function drawBackground(ctx) {
  const grad = ctx.createRadialGradient(
    W * 0.3, 0, 0,
    W * 0.5, H * 0.5, W * 0.9,
  )
  grad.addColorStop(0, COLORS.bgInner)
  grad.addColorStop(0.7, COLORS.bgOuter)
  grad.addColorStop(1, COLORS.bgOuter)
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, W, H)
}

function drawSkewedText(ctx, text, x, y) {
  ctx.save()
  ctx.setTransform(1, 0, SKEW_X, 1, x, y)
  ctx.fillText(text, 0, 0)
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.restore()
}

function drawHeader(ctx, cluster) {
  const y = LAYOUT.headerY
  ctx.font = FONTS.brand
  ctx.textBaseline = 'alphabetic'

  ctx.fillStyle = COLORS.text
  ctx.textAlign = 'left'
  drawSkewedText(ctx, 'СВОЯ ИГРА ФНФ', LAYOUT.paddingX, y)

  ctx.fillStyle = COLORS.muted
  ctx.textAlign = 'right'
  const label = CLUSTER_LABEL[cluster] || cluster.toUpperCase()
  drawSkewedText(ctx, label, W - LAYOUT.paddingX, y)
}

function drawTitle(ctx) {
  ctx.font = FONTS.title
  ctx.fillStyle = COLORS.accent
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  drawSkewedText(ctx, 'РЕЙТИНГ ИГРОКОВ', LAYOUT.paddingX, LAYOUT.titleY + 64)
}

function drawRows(ctx, allPlayers) {
  const top30 = allPlayers.slice(0, 30)
  const left = top30.slice(0, 15)
  const right = top30.slice(15, 30)

  const usableWidth = W - LAYOUT.paddingX * 2
  const colWidth = (usableWidth - LAYOUT.columnGap) / 2

  drawColumn(ctx, left, LAYOUT.paddingX, colWidth, 0)
  drawColumn(ctx, right, LAYOUT.paddingX + colWidth + LAYOUT.columnGap, colWidth, 15)
}

function drawColumn(ctx, players, xLeft, colWidth, placeOffset) {
  for (let i = 0; i < players.length; i++) {
    const player = players[i]
    const place = placeOffset + i + 1
    const yTop = LAYOUT.gridTop + i * LAYOUT.rowHeight
    drawRow(ctx, player, place, xLeft, yTop, colWidth)
  }
}

function drawRow(ctx, player, place, xLeft, yTop, colWidth) {
  const yMid = yTop + LAYOUT.rowHeight / 2
  const yBottom = yTop + LAYOUT.rowHeight

  ctx.strokeStyle = COLORS.divider
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(xLeft, yBottom - 0.5)
  ctx.lineTo(xLeft + colWidth, yBottom - 0.5)
  ctx.stroke()

  const placeColor = topColor(place) || COLORS.muted
  const ratingColor = topColor(place) || COLORS.accent

  ctx.textBaseline = 'middle'

  const placeBoxRight = xLeft + 40
  ctx.font = FONTS.place
  ctx.fillStyle = placeColor
  ctx.textAlign = 'right'
  ctx.fillText(String(place), placeBoxRight, yMid)

  ctx.font = FONTS.gw
  const gwText = `${player.games || 0}/${player.wins || 0}`
  const gwWidth = ctx.measureText(gwText).width

  ctx.font = FONTS.rating
  const ratingText = formatRating(player.rating)
  const ratingWidth = ctx.measureText(ratingText).width

  const rightEdge = xLeft + colWidth
  const gwRight = rightEdge
  const ratingRight = gwRight - gwWidth - 8

  ctx.fillStyle = COLORS.muted
  ctx.font = FONTS.gw
  ctx.textAlign = 'right'
  ctx.fillText(gwText, gwRight, yMid)

  ctx.fillStyle = ratingColor
  ctx.font = FONTS.rating
  ctx.fillText(ratingText, ratingRight, yMid)

  const nameLeft = placeBoxRight + 12
  const nameRight = ratingRight - ratingWidth - 12
  ctx.fillStyle = COLORS.text
  ctx.font = FONTS.name
  ctx.textAlign = 'left'
  const trimmedName = ellipsize(ctx, String(player.name || ''), nameRight - nameLeft)
  ctx.fillText(trimmedName, nameLeft, yMid)
}

function topColor(place) {
  if (place === 1) return COLORS.gold
  if (place === 2) return COLORS.silver
  if (place === 3) return COLORS.bronze
  return null
}

function formatRating(value) {
  const n = Number(value || 0)
  return n.toFixed(2)
}

function ellipsize(ctx, text, maxWidth) {
  if (maxWidth <= 0) return ''
  if (ctx.measureText(text).width <= maxWidth) return text
  const ellipsis = '…'
  let lo = 0
  let hi = text.length
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2)
    const candidate = text.slice(0, mid) + ellipsis
    if (ctx.measureText(candidate).width <= maxWidth) lo = mid
    else hi = mid - 1
  }
  return text.slice(0, lo) + ellipsis
}

function drawFooter(ctx) {
  ctx.font = FONTS.footer
  ctx.fillStyle = COLORS.muted
  ctx.textBaseline = 'alphabetic'

  ctx.textAlign = 'left'
  ctx.fillText(formatToday(), LAYOUT.paddingX, LAYOUT.footerY)

  drawLegend(ctx)
}

function drawLegend(ctx) {
  const y = LAYOUT.footerY
  const rightEdge = W - LAYOUT.paddingX
  const sep = '   ·   '

  ctx.textBaseline = 'alphabetic'
  ctx.textAlign = 'left'

  ctx.font = FONTS.footer
  const labelRating = ' — рейтинг'
  const labelRatingWidth = ctx.measureText(labelRating).width
  const sepWidth = ctx.measureText(sep).width
  const labelGw = ' — игр / побед'
  const labelGwWidth = ctx.measureText(labelGw).width

  ctx.font = FONTS.rating
  const sampleRating = '12.34'
  const sampleRatingWidth = ctx.measureText(sampleRating).width

  ctx.font = FONTS.gw
  const sampleGw = '5/2'
  const sampleGwWidth = ctx.measureText(sampleGw).width

  const totalWidth = sampleRatingWidth + labelRatingWidth + sepWidth + sampleGwWidth + labelGwWidth
  let x = rightEdge - totalWidth

  ctx.font = FONTS.rating
  ctx.fillStyle = COLORS.accent
  ctx.fillText(sampleRating, x, y)
  x += sampleRatingWidth

  ctx.font = FONTS.footer
  ctx.fillStyle = COLORS.muted
  ctx.fillText(labelRating, x, y)
  x += labelRatingWidth

  ctx.fillText(sep, x, y)
  x += sepWidth

  ctx.font = FONTS.gw
  ctx.fillText(sampleGw, x, y)
  x += sampleGwWidth

  ctx.font = FONTS.footer
  ctx.fillText(labelGw, x, y)
}

function formatToday() {
  const d = new Date()
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}.${mm}.${yyyy}`
}

function fileName(cluster) {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `rating-${cluster}-${yyyy}-${mm}-${dd}.png`
}

function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) reject(new Error('canvas.toBlob returned null'))
      else resolve(blob)
    }, 'image/png')
  })
}

function triggerDownload(blob, name) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
